import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

function InstallOmniMarkButton() {
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const onBeforeInstall = (event) => { event.preventDefault(); setInstallPrompt(event); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  async function install() {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  if (!installPrompt) return <Link to="/login"><Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">Open OmniMark</Button></Link>;
  return <Button onClick={install} variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">Install OmniMark</Button>;
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0a0a0f]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500" />
            <span className="text-xl font-bold tracking-tight">OmniMark HQ</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#team" className="hover:text-white transition">AI Team</a>
            <a href="#studio" className="hover:text-white transition">AI Studio</a>
            <a href="#agency" className="hover:text-white transition">Agencies</a>
            <a href="#app-directory" className="hover:text-white transition">App Directory</a>
            <a href="#pricing" className="hover:text-white transition">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
            <Link to="/login">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border-0">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — Dark, Premium
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-violet-600/20 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-fuchsia-600/20 rounded-full blur-[128px]" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50" />
        </div>

        <div className="relative mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm mb-8">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/70">AI-Powered Marketing Platform</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            Your AI Marketing Department
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              Fully Staffed, Always Working
            </span>
          </h1>

          <p className="mx-auto max-w-2xl text-lg sm:text-xl text-white/60 mb-10 leading-relaxed">
            OmniMark HQ deploys AI strategists, creatives, and presenters to build, 
            launch, and optimize ads across every platform — without agencies.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login">
              <Button 
                size="lg" 
                className="h-14 px-8 text-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border-0 shadow-lg shadow-violet-500/25"
              >
                Start 7-Day AI Trial
              </Button>
            </Link>
            <a
              href="#studio"
              className="inline-flex h-14 items-center justify-center rounded-md border border-white/20 bg-white/5 px-8 text-lg font-medium text-white transition-colors hover:bg-white/10"
            >
              Explore AI Studio
            </a>
            <InstallOmniMarkButton />
          </div>

          <div className="mt-16 flex flex-col items-center gap-4">
            <div className="flex -space-x-3">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-10 w-10 rounded-full border-2 border-[#0a0a0f] bg-gradient-to-br from-violet-400 to-fuchsia-400"
                  style={{ opacity: 1 - i * 0.15 }}
                />
              ))}
            </div>
            <p className="text-sm text-white/50">
              Built for marketing teams and agencies
            </p>
          </div>
        </div>
      </section>

      <section id="app-directory" className="border-t border-white/5 bg-gradient-to-b from-violet-950/20 to-transparent px-6 py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.1fr_.9fr]">
          <div>
            <div className="inline-flex rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 px-3 py-1 text-sm text-fuchsia-200">New: App Directory</div>
            <h2 className="mt-5 text-3xl font-bold sm:text-4xl">Discover tools built with real momentum.</h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/60">The OmniMark App Directory brings useful customer-built apps into one public place. Listings link to their live web apps and are scanned by AI first—clean submissions publish automatically, while policy concerns are flagged for review.</p>
            <div className="mt-7 flex flex-wrap gap-3"><Link to="/apps"><Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">Explore App Directory</Button></Link><InstallOmniMarkButton /></div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 shadow-2xl shadow-violet-950/30">
            <div className="rounded-2xl border border-violet-400/20 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 p-6">
              <div className="flex items-center justify-between"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg font-bold">O</span><div><div className="font-semibold">OmniMark HQ</div><div className="text-sm text-white/60">Marketing & AI</div></div></div><span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs text-emerald-200">Official</span></div>
              <p className="mt-6 text-sm leading-relaxed text-white/70">Create campaigns, AI-assisted content, marketing workflows, and governed build blueprints in one web application.</p>
              <div className="mt-6 grid grid-cols-2 gap-3"><Link to="/login"><Button className="w-full bg-white text-slate-950 hover:bg-white/90">Open app</Button></Link><InstallOmniMarkButton /></div>
            </div>
            <p className="mt-4 text-center text-xs text-white/45">Install is shown when your browser supports installing OmniMark as a web app.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          MEET THE AI TEAM — Avatars Grid
      ═══════════════════════════════════════════════════════════════ */}
      <section id="team" className="py-24 px-6 border-t border-white/5">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Meet the AI Team
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Six specialized AI agents working together on every campaign
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: "Cole", role: "Ad Creator", color: "from-orange-500 to-red-500", canPresent: true },
              { name: "Maya", role: "Strategist", color: "from-blue-500 to-cyan-500", canPresent: false },
              { name: "Nova", role: "Social", color: "from-pink-500 to-rose-500", canPresent: true },
              { name: "River", role: "Coach", color: "from-emerald-500 to-teal-500", canPresent: true },
              { name: "Atlas", role: "Brand", color: "from-violet-500 to-purple-500", canPresent: false },
              { name: "Echo", role: "Sales", color: "from-amber-500 to-yellow-500", canPresent: false },
            ].map((p, i) => (
              <div key={i} className="group text-center">
                <div className={`mx-auto h-20 w-20 rounded-full bg-gradient-to-br ${p.color} flex items-center justify-center text-2xl font-bold mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {p.name[0]}
                </div>
                <div className="font-semibold">{p.name}</div>
                <div className="text-sm text-white/50">{p.role}</div>
                {p.canPresent && (
                  <div className="text-xs text-violet-400 mt-1">🎬 Presenter</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          WHAT THEY DO — 4 Capabilities
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 border-t border-white/5 bg-gradient-to-b from-transparent to-violet-950/10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What They Do
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Your AI team handles every aspect of marketing
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Create Ads",
                desc: "Scripts, hooks, headlines, and variations that convert across every platform.",
                icon: "✨",
                gradient: "from-orange-500/20 to-transparent",
              },
              {
                title: "Present on Camera",
                desc: "AI presenters deliver your message with avatar videos and voice synthesis.",
                icon: "🎬",
                gradient: "from-pink-500/20 to-transparent",
              },
              {
                title: "Run Campaigns",
                desc: "Strategy, targeting, budgets, and launch plans built by AI strategists.",
                icon: "🚀",
                gradient: "from-blue-500/20 to-transparent",
              },
              {
                title: "Optimize Daily",
                desc: "Continuous improvement based on performance data and market trends.",
                icon: "📈",
                gradient: "from-emerald-500/20 to-transparent",
              },
            ].map((f, i) => (
              <div 
                key={i}
                className={`rounded-2xl border border-white/10 bg-gradient-to-b ${f.gradient} p-6 hover:border-white/20 transition-all duration-300`}
              >
                <div className="text-4xl mb-4">{f.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          AI STUDIO
      ═══════════════════════════════════════════════════════════════ */}
      <section id="studio" className="py-24 px-6 border-t border-white/5">
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-sm text-violet-400 mb-6">
                AI Studio
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                The command center for
                <br />
                <span className="text-violet-400">AI-powered campaigns</span>
              </h2>
              <p className="text-white/60 mb-8 leading-relaxed">
                Build custom AI workflows, run packs of agents, and generate complete ad campaigns in minutes. Spokesperson mode creates video-ready scripts with avatar and voice assignments.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Drag-and-drop AI sequence builder",
                  "Spokesperson mode with video scripts",
                  "Credit cost estimation before running",
                  "Save and reuse custom pack templates",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/70">
                    <span className="h-5 w-5 rounded-full bg-violet-500/20 flex items-center justify-center text-xs text-violet-400">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border-0">
                  Try AI Studio Free
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-3">
                  <div className="h-8 w-full rounded bg-white/5" />
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-20 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-500/5 border border-orange-500/20 flex items-center justify-center text-2xl">C</div>
                    <div className="h-20 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/20 flex items-center justify-center text-2xl">M</div>
                    <div className="h-20 rounded-lg bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-500/20 flex items-center justify-center text-2xl">N</div>
                  </div>
                  <div className="h-10 w-full rounded bg-gradient-to-r from-violet-600 to-fuchsia-600 flex items-center justify-center text-sm font-medium">Run Pack → 15 credits</div>
                </div>
              </div>
              <div className="absolute -z-10 inset-0 bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          AGENCY-READY
      ═══════════════════════════════════════════════════════════════ */}
      <section id="agency" className="py-24 px-6 border-t border-white/5 bg-gradient-to-b from-violet-950/10 to-transparent">
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "White-Label", desc: "Your brand, your platform", icon: "🏷️" },
                  { label: "Team Roles", desc: "Owner, Admin, Client", icon: "👥" },
                  { label: "Pack Sharing", desc: "Share across workspaces", icon: "📤" },
                  { label: "Marketplace", desc: "Pre-built AI workflows", icon: "📦" },
                ].map((f, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="text-2xl mb-2">{f.icon}</div>
                    <div className="font-semibold text-sm">{f.label}</div>
                    <div className="text-xs text-white/50">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-400 mb-6">
                Agency Ready
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Built for agencies
                <br />
                <span className="text-emerald-400">at scale</span>
              </h2>
              <p className="text-white/60 mb-8 leading-relaxed">
                White-label the entire platform with your branding. Manage multiple client workspaces, assign team roles, and share AI packs across accounts. Everything agencies need to offer AI marketing as a service.
              </p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white border-0">
                  Agency Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          PRICING
      ═══════════════════════════════════════════════════════════════ */}
      <section id="pricing" className="py-24 px-6 border-t border-white/5">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-white/50">
              Start free, scale as you grow
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                name: "Starter", 
                price: "$0", 
                period: "7-day trial",
                features: ["100 AI credits", "1 workspace", "Core AI agents", "Email support"],
                cta: "Start Trial",
                popular: false,
                gradient: "",
              },
              { 
                name: "Pro", 
                price: "$49", 
                period: "/month",
                features: ["1,000 AI credits/mo", "5 workspaces", "All AI agents", "Spokesperson mode", "Priority support"],
                cta: "Get Pro",
                popular: true,
                gradient: "from-violet-600 to-fuchsia-600",
              },
              { 
                name: "Agency", 
                price: "$199", 
                period: "/month",
                features: ["5,000 AI credits/mo", "Unlimited workspaces", "White-label branding", "Team members", "API access"],
                cta: "Contact Sales",
                popular: false,
                gradient: "",
              },
            ].map((plan, i) => (
              <div 
                key={i}
                className={`rounded-2xl border p-6 ${
                  plan.popular 
                    ? "border-violet-500/50 bg-gradient-to-b from-violet-500/10 to-transparent" 
                    : "border-white/10 bg-white/[0.02]"
                }`}
              >
                {plan.popular && (
                  <div className="text-xs font-medium text-violet-400 mb-4">MOST POPULAR</div>
                )}
                <div className="text-lg font-semibold mb-1">{plan.name}</div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-white/50">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                      <span className="text-emerald-400">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/login">
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border-0" 
                        : "bg-white/10 hover:bg-white/20 text-white border-0"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FOOTER — Legal
      ═══════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-white/10 py-12 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500" />
                <span className="font-semibold">OmniMark HQ</span>
              </div>
              <p className="text-sm text-white/50">
                Your AI marketing department, fully staffed and always working.
              </p>
            </div>
            <div>
              <div className="font-semibold mb-4">Product</div>
              <ul className="space-y-2 text-sm text-white/50">
                <li><a href="#team" className="hover:text-white transition">AI Team</a></li>
                <li><a href="#studio" className="hover:text-white transition">AI Studio</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><Link to="/login" className="hover:text-white transition">Login</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-4">Company</div>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
                <li><a href="#agency" className="hover:text-white transition">About</a></li>
                <li><Link to="/contact" className="hover:text-white transition">Blog requests</Link></li>
                <li><Link to="/contact" className="hover:text-white transition">Careers</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-4">Legal</div>
              <ul className="space-y-2 text-sm text-white/50">
                <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
                <li><Link to="/refund" className="hover:text-white transition">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-white/30">
              © 2026 OmniMark HQ. All rights reserved.
            </div>
            <div className="flex gap-4">
              <a href="https://x.com" target="_blank" rel="noreferrer" aria-label="Open X" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:bg-white/10 hover:text-white transition">
                𝕏
              </a>
              <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="Open LinkedIn" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:bg-white/10 hover:text-white transition">
                in
              </a>
              <a href="https://www.youtube.com" target="_blank" rel="noreferrer" aria-label="Open YouTube" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:bg-white/10 hover:text-white transition">
                yt
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
